import { useCallback, useMemo, useState } from 'react';

import { filterBySmartSearch, type SearchStrictness, type SmartSearchFields } from '@/lib/search/smartSearch';

/**
 * Preferência de rigor compartilhada pelas telas com busca inteligente. Não é
 * dado do usuário (só um modo de casar texto), então a chave não precisa de
 * escopo por conta; o storage pode faltar (modo privado), daí o try/catch.
 */
const STRICTNESS_STORAGE_KEY = 'jurisapp:smart-search:strictness';

function readStrictness(): SearchStrictness {
  try {
    return localStorage.getItem(STRICTNESS_STORAGE_KEY) === 'strict' ? 'strict' : 'flexible';
  } catch {
    return 'flexible';
  }
}

function writeStrictness(value: SearchStrictness): void {
  try {
    localStorage.setItem(STRICTNESS_STORAGE_KEY, value);
  } catch {
    // Sem persistência — a escolha vale só enquanto a tela estiver aberta.
  }
}

export interface SmartSearchState<T> {
  strictness: SearchStrictness;
  setStrictness: (value: SearchStrictness) => void;
  /** A coleção filtrada — igual à de entrada quando a pergunta está vazia. */
  results: T[];
  /** Tamanho da coleção de entrada, para o "N de M". */
  total: number;
  /** Há pergunta (não vazia)? */
  active: boolean;
}

/**
 * Só o rigor, guardado como nas outras telas — para a busca feita no SERVIDOR
 * (Comunicações), que manda o rigor junto com a pergunta.
 */
export function useRigorDaBusca(): [SearchStrictness, (value: SearchStrictness) => void] {
  const [strictness, setStrictnessState] = useState<SearchStrictness>(readStrictness);
  const setStrictness = useCallback((value: SearchStrictness) => {
    setStrictnessState(value);
    writeStrictness(value);
  }, []);
  return [strictness, setStrictness];
}

/**
 * Filtra uma lista que JÁ está carregada inteira na tela. A pergunta é do
 * chamador (pode morar na URL); o hook guarda só o rigor.
 *
 * `fields` deve ser estável (constante de módulo ou `useMemo`) — senão o filtro
 * refaz a cada render.
 */
export function useSmartSearch<T>(
  items: readonly T[],
  query: string,
  fields: SmartSearchFields<T>,
): SmartSearchState<T> {
  const [strictness, setStrictness] = useRigorDaBusca();

  const results = useMemo(
    () => filterBySmartSearch(items, query, strictness, fields),
    [items, query, strictness, fields],
  );

  return {
    strictness,
    setStrictness,
    results,
    total: items.length,
    active: query.trim().length > 0,
  };
}
